import { useEffect, useState } from "react";
import { docs } from "@/lib/api";
import { Shell } from "@/components/Shell";
import {
  FileText, CheckCircle2, Clock, PenLine, Eye, X, Sparkles,
  ShieldCheck, Filter
} from "lucide-react";
import { toast } from "sonner";

export default function Documentos() {
  const [documentos, setDocumentos] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const [d, dsh, cats] = await Promise.all([docs.list(), docs.dashboard(), docs.categorias()]);
    setDocumentos(d.data); setDashboard(dsh.data); setCategorias(cats.data);
  };

  useEffect(() => { load(); }, []);

  const filtered = filter ? documentos.filter(d => d.categoria === filter) : documentos;

  const abrir = async (doc) => {
    setSelected(doc);
    if (!doc.lido) {
      try { await docs.ler(doc.id); load(); } catch {}
    }
  };

  const assinar = async () => {
    try {
      await docs.assinar(selected.id);
      toast.success("Documento assinado digitalmente");
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <Shell>
      <div className="p-6 md:p-10 fade-up max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="text-[#B28F4B] text-[10px] font-bold tracking-[0.3em] uppercase mb-3">
            Documentos & Integração
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-light text-white">
            Governança <span className="text-[#B28F4B] italic font-serif">documental</span>
          </h1>
          <p className="text-[#A1A4C7] mt-2">Leia, assine e acompanhe seus documentos institucionais.</p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat icon={FileText} label="Total publicado" value={dashboard?.total ?? "—"} color="#818CF8"/>
          <Stat icon={Clock} label="Não lidos" value={dashboard?.nao_lidos ?? "—"} color="#FB923C"/>
          <Stat icon={PenLine} label="Pendente assinatura" value={dashboard?.pendente_assinatura ?? "—"} color="#F87171"/>
          <Stat icon={CheckCircle2} label="Obrigatórios" value={dashboard?.obrigatorios ?? "—"} color="#34D399"/>
        </div>

        {/* Filtros */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          <div className="text-[#A1A4C7] text-xs uppercase tracking-widest font-bold flex items-center gap-1 shrink-0">
            <Filter size={13}/> Categoria:
          </div>
          <button onClick={() => setFilter("")} className={`text-xs px-3 py-1.5 rounded-full font-semibold whitespace-nowrap ${filter === "" ? "bg-[#B28F4B] text-[#1A1D42]" : "bg-[#131532] text-[#A1A4C7]"}`}>Todas</button>
          {categorias.slice(0, 8).map(c => (
            <button key={c.id} onClick={() => setFilter(c.nome)} className={`text-xs px-3 py-1.5 rounded-full font-semibold whitespace-nowrap ${filter === c.nome ? "bg-[#B28F4B] text-[#1A1D42]" : "bg-[#131532] text-[#A1A4C7]"}`}>{c.nome}</button>
          ))}
        </div>

        <div className="grid gap-3">
          {filtered.length === 0 && <div className="soeasy-card p-10 text-center text-[#A1A4C7]">Nenhum documento nesta categoria</div>}
          {filtered.map(d => (
            <div
              key={d.id}
              onClick={() => abrir(d)}
              data-testid={`doc-item-${d.id}`}
              className="soeasy-card p-5 cursor-pointer flex items-start md:items-center gap-4 flex-col md:flex-row group"
            >
              <div className="w-11 h-11 rounded-xl bg-[#B28F4B]/15 border border-[#B28F4B]/30 flex items-center justify-center flex-shrink-0">
                <FileText size={18} className="text-[#B28F4B]"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <div className="text-white font-semibold group-hover:text-[#B28F4B] transition-colors">{d.titulo}</div>
                  {d.requer_assinatura && (
                    <span className="text-[9px] uppercase tracking-widest font-bold text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-full">Assinatura obrigatória</span>
                  )}
                </div>
                <div className="text-[#A1A4C7] text-xs mb-1">{d.descricao}</div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#6B6E8F]">{d.categoria} · {d.tipo}</div>
              </div>
              <div className="flex flex-wrap md:flex-col items-center md:items-end gap-2">
                {d.lido ? (
                  <span className="text-[9px] uppercase tracking-widest font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full flex items-center gap-1"><Eye size={9}/> Lido</span>
                ) : (
                  <span className="text-[9px] uppercase tracking-widest font-bold text-orange-400 bg-orange-500/10 border border-orange-500/20 px-2 py-0.5 rounded-full">Não lido</span>
                )}
                {d.requer_assinatura && (
                  d.assinado ? (
                    <span className="text-[9px] uppercase tracking-widest font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle2 size={9}/> Assinado</span>
                  ) : (
                    <span className="text-[9px] uppercase tracking-widest font-bold text-[#B28F4B] bg-[#B28F4B]/10 border border-[#B28F4B]/30 px-2 py-0.5 rounded-full">Pendente</span>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal viewer */}
      {selected && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 fade-up" onClick={() => setSelected(null)}>
          <div onClick={e => e.stopPropagation()} className="soeasy-card max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-start justify-between p-6 border-b border-[#B28F4B]/15">
              <div>
                <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#B28F4B] mb-2">{selected.categoria} · {selected.tipo}</div>
                <h2 className="font-heading text-2xl font-medium text-white">{selected.titulo}</h2>
                {selected.descricao && <p className="text-[#A1A4C7] text-sm mt-1">{selected.descricao}</p>}
              </div>
              <button onClick={() => setSelected(null)} className="text-[#A1A4C7] hover:text-white p-1"><X size={20}/></button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="text-white text-sm leading-relaxed whitespace-pre-wrap">{selected.conteudo || "Documento sem conteúdo disponível."}</div>
            </div>
            {selected.requer_assinatura && !selected.assinado && (
              <div className="p-4 border-t border-[#B28F4B]/15 bg-[#0A0B1A] flex items-center justify-between gap-3">
                <div className="text-xs text-[#A1A4C7]">Ao assinar você confirma leitura e ciência do conteúdo.</div>
                <button onClick={assinar} data-testid="btn-assinar-documento" className="inline-flex items-center gap-2 bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold px-5 py-2.5 rounded-lg text-sm">
                  <ShieldCheck size={14}/> Assinar digitalmente
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </Shell>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="soeasy-card p-5">
      <div
        className="w-9 h-9 rounded-lg mb-3 flex items-center justify-center"
        style={{ background: `${color}18`, border: `1px solid ${color}30` }}
      >
        <Icon size={17} style={{ color }} strokeWidth={1.7} />
      </div>
      <div className="font-heading text-3xl font-light text-white mb-1">{value}</div>
      <div className="text-[10px] text-[#A1A4C7] uppercase tracking-widest font-semibold">{label}</div>
    </div>
  );
}
